import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import RFB from '@novnc/novnc/lib/rfb';
import './ConsolePage.css';

function ConsolePage() {
  const { vmid } = useParams();
  const [consoleInfo, setConsoleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [vncError, setVncError] = useState('');
  const [wsUrl, setWsUrl] = useState('');

  const displayRef = useRef(null);
  const rfbRef = useRef(null);
  const vncTicketRef = useRef('');
  const autoOpenedRef = useRef(false);
  const blackScreenTimerRef = useRef(null);

  const reportConsoleEvent = (event, details) => {
    axios.post('/api/logs/console/report', { vmid, event, details }).catch(() => {});
  };

  useEffect(() => {
    fetchConsoleInfo();
  }, [vmid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Abre o console automaticamente ao carregar, se a VM estiver rodando
  useEffect(() => {
    if (!loading && consoleInfo && consoleInfo.status !== 'stopped' && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      openConsoleEmbedded();
    }
  }, [loading, consoleInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // noVNC lifecycle: recreate RFB whenever wsUrl changes
  useEffect(() => {
    if (!wsUrl || !displayRef.current) return;

    setConnecting(true);
    setVncError('');

    const rfb = new RFB(displayRef.current, wsUrl, {
      credentials: { password: vncTicketRef.current },
    });
    rfb.scaleViewport = true;
    rfb.resizeSession = false;

    rfb.addEventListener('connect', () => {
      setConnecting(false);
      reportConsoleEvent('connect', `VM ${vmid} conectada`);

      // Detecção de tela preta: após 5s verifica pixels do canvas
      if (blackScreenTimerRef.current) clearTimeout(blackScreenTimerRef.current);
      blackScreenTimerRef.current = setTimeout(() => {
        try {
          const canvas = displayRef.current?.querySelector('canvas');
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
          let allBlack = true;
          for (let i = 0; i < data.length; i += 16) {
            if (data[i] > 5 || data[i + 1] > 5 || data[i + 2] > 5) {
              allBlack = false;
              break;
            }
          }
          if (allBlack) {
            reportConsoleEvent('black_screen', `Tela preta detectada após 5s na VM ${vmid}`);
          }
        } catch (_) { /* canvas cross-origin ou não disponível */ }
      }, 5000);
    });

    rfb.addEventListener('disconnect', (e) => {
      setConnecting(false);
      if (blackScreenTimerRef.current) clearTimeout(blackScreenTimerRef.current);
      if (!e.detail.clean) {
        setVncError('Conexão VNC encerrada inesperadamente.');
        reportConsoleEvent('unexpected_disconnect', `VM ${vmid} desconectou inesperadamente`);
      }
    });

    rfb.addEventListener('credentialsrequired', () => {
      rfb.sendCredentials({ password: vncTicketRef.current });
    });

    rfbRef.current = rfb;

    return () => {
      if (blackScreenTimerRef.current) clearTimeout(blackScreenTimerRef.current);
      rfb.disconnect();
      rfbRef.current = null;
    };
  }, [wsUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConsoleInfo = async () => {
    try {
      setLoading(true);
      setError('');
      const [vmRes, consoleRes] = await Promise.allSettled([
        axios.get(`/api/vms/${vmid}`),
        axios.get(`/api/vms/${vmid}/console`)
      ]);

      const vmData = vmRes.status === 'fulfilled' ? vmRes.value.data?.data || vmRes.value.data : null;
      const consoleData = consoleRes.status === 'fulfilled' ? consoleRes.value.data : null;

      if (!vmData && !consoleData) {
        setError('Não foi possível carregar informações da VM.');
      } else {
        setConsoleInfo({
          node: consoleData?.node || vmData?.node,
          vmid: vmid,
          status: vmData?.status,
          name: vmData?.name,
          proxmoxHost: consoleData?.proxmoxHost,
          ticket: consoleData?.consoleData,
          ticketError: consoleData?.ticketError
        });
      }
    } catch (err) {
      setError('Erro ao carregar informações da VM.');
      if (process.env.NODE_ENV !== 'production') console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openConsoleEmbedded = async () => {
    try {
      setVncError('');

      // Obter ticket VNC fresco
      const res = await axios.get(`/api/vms/${vmid}/console`);
      const { node, consoleData } = res.data;

      if (!consoleData?.ticket || !consoleData?.port) {
        setVncError('Não foi possível obter ticket VNC do Proxmox.');
        return;
      }

      const url =
        `wss://${window.location.host}/ws/vnc` +
        `?vmid=${vmid}&node=${node}&port=${consoleData.port}` +
        `&ticket=${encodeURIComponent(consoleData.ticket)}`;

      // Salva o ticket para autenticação VNC (Proxmox usa o ticket como senha VNC)
      vncTicketRef.current = consoleData.ticket;

      // Mostrar container antes de criar RFB (garante que displayRef.current esteja disponível)
      setConsoleOpen(true);
      setWsUrl(url);
    } catch (err) {
      setVncError('Erro ao iniciar console VNC.');
      if (process.env.NODE_ENV !== 'production') console.error(err);
    }
  };

  const sendCtrlAltDel = () => {
    if (rfbRef.current) {
      rfbRef.current.sendCtrlAltDel();
    }
  };

  const closeConsole = () => {
    // Desconecta imediatamente e reseta wsUrl → dispara cleanup do useEffect
    if (rfbRef.current) {
      rfbRef.current.disconnect();
      rfbRef.current = null;
    }
    vncTicketRef.current = '';
    setConsoleOpen(false);
    setWsUrl('');
    setVncError('');
    setConnecting(false);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-spinner">Carregando informações da VM...</div>
        </div>
      </div>
    );
  }

  if (error && !consoleInfo) {
    return (
      <div className="page">
        <div className="container">
          <div className="console-header">
            <Link to={`/vm/${vmid}`} className="back-link">← Voltar</Link>
          </div>
          <div className="error-message">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const isStopped = consoleInfo?.status === 'stopped';

  return (
    <div className="page console-page">
      <div className="container console-container">
        <div className="console-header">
          <Link to={`/vm/${vmid}`} className="back-link">← Voltar</Link>
          <h1>Console — VM {consoleInfo?.name || vmid}</h1>
          <div className="console-header-actions">
            {!consoleOpen ? (
              <button
                className="console-btn primary"
                onClick={openConsoleEmbedded}
                disabled={isStopped}
                title={isStopped ? 'VM está parada' : ''}
              >
                🖥️ Abrir Console
              </button>
            ) : (
              <>
                <button
                  className="console-btn secondary"
                  onClick={sendCtrlAltDel}
                  title="Enviar Ctrl+Alt+Del para a VM"
                >
                  Ctrl+Alt+Del
                </button>
                <button
                  className="console-btn danger"
                  onClick={closeConsole}
                >
                  ✕ Fechar Console
                </button>
              </>
            )}
            <button className="refresh-btn" onClick={fetchConsoleInfo}>
              ⟳ Atualizar
            </button>
          </div>
        </div>

        <div className="console-info">
          <div className="console-card">

            {isStopped && (
              <div className="warning-message">
                ⚠️ A VM está parada. O console pode não estar disponível.
              </div>
            )}

            {connecting && (
              <div className="warning-message">
                Conectando ao console VNC...
              </div>
            )}

            {vncError && (
              <div className="error-message" style={{ marginBottom: '1rem' }}>
                <p>{vncError}</p>
              </div>
            )}

            {consoleOpen && (
              <div className="vnc-display-container" ref={displayRef} />
            )}

            <div className="info-section">
              <h3>Informações</h3>
              <div className="console-details">
                <div className="detail-item">
                  <label>Nó:</label>
                  <div className="detail-value">
                    <code>{consoleInfo?.node || '—'}</code>
                  </div>
                </div>
                <div className="detail-item">
                  <label>VMID:</label>
                  <div className="detail-value">
                    <code>{vmid}</code>
                  </div>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  <div className="detail-value">
                    <code>{consoleInfo?.status || '—'}</code>
                  </div>
                </div>
              </div>
            </div>

            {consoleInfo?.ticketError && (
              <div className="info-section">
                <p className="ticket-note">
                  ℹ️ {consoleInfo.ticketError}
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default ConsolePage;
